import { lazy, Suspense, useMemo } from "react";
import { getCurrentYear } from "@/utils/dateUtils";
import { ReviewedBy } from "@/components/ReviewedBy";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { StickyCTA } from "@/components/StickyCTA";
import { useStickyCTA } from "@/hooks/useStickyCTA";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldAlert, CheckCircle2, XCircle, Info, ArrowRight, AlertTriangle, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { DynamicDetailedLoanCard } from "@/components/DynamicDetailedLoanCard";
import brixoLogo from "@/assets/brixo-logo.webp";
import fairloLogo from "@/assets/fairlo-logga.webp";
import ferratumLogo from "@/assets/ferratum-logo.webp";
import flexkontotLogo from "@/assets/flexkontot-logo.webp";
import loanstepLogo from "@/assets/loanstep_ny_compress.webp";
import meraxLogo from "@/assets/merax-logo.webp";
import { DynamicFeaturedProductCard } from "@/components/DynamicFeaturedProductCard";
import { LenderLiveDataStrip } from "@/components/LenderLiveDataStrip";
import { useProviderLiveTermsMap } from "@/hooks/useProviderLiveTerms";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ComparisonMethodology } from "@/components/content/ComparisonMethodology";
import { DecisionPathLinks } from "@/components/content/DecisionPathLinks";
import { generateComparisonPageSchema } from "@/utils/schemaGenerators";
import { applyLiveTermsToComparisonItems, getLatestLiveTermsUpdatedAt, getLiveTermsVerifiedLabel } from "@/utils/providerLiveTerms";

const Footer = lazy(() => import("@/components/Footer"));

const loanWithoutUCLiveSlugs = ["ferratum", "fairlo", "loanstep", "brixo", "flexkontot", "merax"];

const loanWithoutUCSchemaItems = [
  {
    name: "Loanstep",
    provider: "Loanstep",
    url: "/sv-SE/lana-utan-uc/loanstep",
    description: "Lån utan UC-påverkan med Creditsafe som kreditupplysning.",
    amount: "10 000 - 50 000 kr",
    interestRate: "21,60 % nominell ränta",
    term: "57 - 76 månader",
    creditCheck: "Creditsafe",
  },
  {
    name: "Fairlo",
    provider: "Fairlo",
    url: "/sv-SE/lana-utan-uc/fairlo",
    description: "Kontokredit utan UC-påverkan.",
    amount: "3 000 - 70 000 kr",
    interestRate: "23,99 - 43,43 % effektiv ränta",
    term: "1 månad - 6 år",
    creditCheck: "Creditsafe",
  },
  {
    name: "Ferratum",
    provider: "Ferratum",
    url: "/sv-SE/lana-utan-uc/ferratum",
    description: "Mindre lån utan UC med snabb digital ansökan.",
    amount: "1 000 - 45 000 kr",
    interestRate: "41,18 - 52,57 % effektiv ränta",
    term: "Flexibel",
    creditCheck: "Creditsafe",
  },
  {
    name: "Brixo",
    provider: "Brixo",
    url: "/sv-SE/lana-utan-uc/brixo",
    description: "Kontokredit utan UC-påverkan för mindre belopp.",
    amount: "3 000 - 50 000 kr",
    interestRate: "Individuell ränta",
    term: "Flexibel",
    creditCheck: "Bisnode",
  },
  {
    name: "Flexkontot",
    provider: "Flexkontot",
    url: "/sv-SE/lana-utan-uc/flexkontot",
    description: "Flexibel kontokredit utan UC-påverkan.",
    amount: "3 000 - 40 000 kr",
    interestRate: "Individuell ränta",
    term: "Flexibel",
    creditCheck: "Creditsafe",
  },
  {
    name: "Merax",
    provider: "Merax",
    url: "/sv-SE/lana-utan-uc/merax",
    description: "Lån utan UC-påverkan med digital ansökan.",
    amount: "2 000 - 30 000 kr",
    interestRate: "Individuell ränta",
    term: "Flexibel",
    creditCheck: "Creditsafe",
  },
];

const loanWithoutUCSchemaBase = {
  name: "Jämför lån utan UC-påverkan",
  description: "Klarfinans jämför lån utan UC-påverkan med fokus på effektiv ränta, lånebelopp, löptid och vilket kreditupplysningsföretag som används.",
  url: "/sv-SE/lana-utan-uc",
  category: "Lån utan UC",
  breadcrumbs: [
    { name: "Start", url: "https://klarfinans.se" },
    { name: "Lån utan UC" },
  ],
};

const LoanWithoutUCPage = () => {
  const navigate = useNavigate();
  const {
    currentLanguage
  } = useLanguage();
  const currentYear = getCurrentYear();
  const { data: liveTermsByUcSlug = {} } = useProviderLiveTermsMap("lan-utan-uc", loanWithoutUCLiveSlugs);
  const loanstepVerifiedLabel = getLiveTermsVerifiedLabel(liveTermsByUcSlug.loanstep ?? null);
  const showStickyCTA = useStickyCTA();
  const loanWithoutUCSchemas = useMemo(
    () => generateComparisonPageSchema({
      ...loanWithoutUCSchemaBase,
      items: applyLiveTermsToComparisonItems(loanWithoutUCSchemaItems, liveTermsByUcSlug),
      updatedAt: getLatestLiveTermsUpdatedAt(liveTermsByUcSlug),
    }),
    [liveTermsByUcSlug]
  );
  
  return <div className="min-h-screen">
      <SEO 
        title={`Lån utan UC - Låna utan UC-påverkan ${currentYear}`}
        description="Jämför lån utan UC-påverkan. Skydda ditt kreditbetyg vid mindre lån. Snabb utbetalning och svar direkt på skärmen."
        keywords={["lån utan UC", "låna utan UC", "lån utan kreditprövning", "Creditsafe", "Bisnode", "blancolån"]}
        schemaData={loanWithoutUCSchemas}
      />
      
      <section className="relative overflow-hidden text-white pt-20 lg:pt-28 pb-8 md:pb-12 lg:pb-32" style={{ backgroundColor: "hsl(var(--primary-dark))" }}>
        <div className="container mx-auto px-3 md:px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-start">
            {/* Left: Text Content + Info Cards */}
            <div className="space-y-4 md:space-y-6">
              <Badge className="mb-4 bg-white/10 text-white border-white/20" variant="outline">
                <ShieldAlert className="w-4 h-4 mr-2" />
                Lån utan UC
              </Badge>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-white lg:leading-[1.05] lg:tracking-tight">
                Låna utan UC-påverkan
              </h1>
              <p className="text-base md:text-xl mb-6 md:mb-8 text-white/90">
                Skydda ditt kreditbetyg vid mindre lån. Jämför lån som använder alternativa kreditupplysningar.
              </p>

              {/* Info badges */}
              <div className="grid grid-cols-1 gap-3 md:gap-4">
              {[{
                icon: ShieldAlert,
                title: 'Skyddar UC-score',
                subtitle: 'Ingen påverkan på UC',
                variant: 'success'
              }, {
                icon: CheckCircle2,
                title: 'Snabb utbetalning',
                subtitle: 'Inom 24h',
                variant: 'info'
              }, {
                icon: XCircle,
                title: 'Begränsat belopp',
                subtitle: 'Max 70 000 kr',
                variant: 'warning'
              }].map((item, i) => <div 
                key={i}
                className="flex items-center gap-2 md:gap-3"
              >
                    <div className="bg-white/10 backdrop-blur-lg rounded-lg p-2 md:p-3 border border-white/20">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-sm text-white/70">{item.subtitle}</p>
                    </div>
                  </div>)}
              </div>

              <div className="hero-cta-section pt-4 flex flex-col gap-3 max-w-md">
                <Button
                  size="lg"
                  className="bg-white text-black hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base px-3 sm:px-4"
                  onClick={() => {
                    const compareSection = document.getElementById('jamfor-lan-utan-uc');
                    if (compareSection) {
                      compareSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  <CheckCircle2 className="mr-1 sm:mr-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="truncate">Jämför handplockade långivare</span>
                  <ArrowRight className="ml-1 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
                <Button 
                  size="lg" 
                  className="bg-white text-black hover:bg-white/90 shadow-lg hover:shadow-xl transition-all duration-300 group text-sm sm:text-base px-3 sm:px-4"
                  onClick={() => navigate(`/${currentLanguage}/guider/lana-utan-uc`)}
                >
                  <Info className="mr-1 sm:mr-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="truncate">Fullständig guide om lån utan UC</span>
                  <ArrowRight className="ml-1 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </div>
            </div>

            {/* Right: Featured Product Card */}
            <div className="max-w-lg mx-auto lg:mx-0 lg:ml-12 lg:max-w-xl pt-4 md:pt-0">
              <div className="space-y-3 md:space-y-4">
                <div className="text-center pt-4 mb-4 md:mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white">Årets bästa lån utan UC-påverkan {currentYear}</h3>
                  <p className="text-white/90 text-sm">{loanstepVerifiedLabel}</p>
                </div>
                
                <DynamicFeaturedProductCard 
                  product={{
                    id: 'loanstep-uc',
                    provider: 'Loanstep',
                    logo: loanstepLogo,
                    rate: '21,60%',
                    amount: '10 000 – 50 000 kr',
                    features: [
                      'Ingen UC-påverkan via Creditsafe',
                      'Löptid 57-76 månader',
                      'Svar direkt på skärmen'
                    ],
                    rating: 4.7,
                    link: 'https://go.adt284.net/t/t?a=1961010341&as=2024680921&t=2&tk=1',
                    product_type: 'private_loan'
                  }} 
                  badge="Ingen UC-påverkan"
                  entityType="lan-utan-uc"
                  entitySlug="loanstep"
                  fallbackRating={4.7}
                  fallbackReviewCount={3494}
                  liveTermsOverride={liveTermsByUcSlug.loanstep ?? null}
                  primaryButtonText="Ansök hos LoanStep"
                  secondaryButtonText="Läs mer om LoanStep"
                  secondaryButtonLink={`/${currentLanguage}/lana-utan-uc/loanstep`}
                />

                <LenderLiveDataStrip
                  entityType="lan-utan-uc"
                  entitySlug="loanstep"
                  className="justify-center"
                  liveTermsOverride={liveTermsByUcSlug.loanstep ?? null}
                />


                <Alert className="border-amber-500/50 bg-amber-500">
                  <Info className="h-4 w-4 md:h-5 md:w-5 text-amber-500" />
                  <AlertDescription className="text-xs">
                    <strong>Obs:</strong> Lån utan UC-påverkan har ofta högre räntor och lägre 
                    maxbelopp än traditionella lån. Lämpar sig bäst för mindre belopp och 
                    kortare återbetalningstider.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12 lg:py-16">
        <div className="container max-w-4xl px-3 md:px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center">
            Vad är lån utan UC-påverkan?
          </h2>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-success" />
                  Fördelar
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-success">✓</span>
                    <span>Ingen påverkan på UC-score</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success">✓</span>
                    <span>Snabb handläggning (ofta samma dag)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success">✓</span>
                    <span>Lättare att få godkänt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-success">✓</span>
                    <span>Skyddar framtida lånemöjligheter</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-destructive" />
                  Nackdelar
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-destructive">✗</span>
                    <span>Högre räntor än traditionella lån</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive">✗</span>
                    <span>Oftast max 70 000 kr</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive">✗</span>
                    <span>Kortare återbetalningstider</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-destructive">✗</span>
                    <span>Färre långivare att välja mellan</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Alert className="border-primary/50 bg-primary/5">
            <Info className="h-5 w-5 text-primary" />
            <AlertDescription>
              <strong>Tips:</strong> Om du planerar att ansöka om bolån, billån eller 
              större lån inom det närmaste året kan lån utan UC-påverkan vara ett 
              smart val för mindre akuta behov.
            </AlertDescription>
          </Alert>
        </div>
      </section>

      <section id="jamfor-lan-utan-uc" className="py-8 md:py-12 lg:py-16 -mt-10 bg-background">
        <div className="container pl-safe-left pr-safe-right max-w-6xl mx-auto">
          <div 
            className="text-center mb-8 animate-fade-in"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Jämför handplockade långivare med högst betyg</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Vi jämför långivare som använder alternativa kreditupplysningsföretag så du kan 
              låna utan att påverka ditt UC-score.
            </p>
          </div>
          
          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <div className="animate-fade-in">
              <DynamicDetailedLoanCard
                provider="Ferratum"
                logo={ferratumLogo}
                loanAmount="1 000 – 45 000 kr"
                nominalRate="22,00 %"
                effectiveRate="41,18 – 52,57 %"
                loanTerm="Flexibel"
                loanWithoutUC="Ja"
                responseTime="Direkt på skärmen"
                paymentSolution="Trustly"
                link={`/${currentLanguage}/lana-utan-uc/ferratum`}
                entityType="lan-utan-uc"
                entitySlug="ferratum"
                fallbackRating={4.4}
                fallbackReviewCount={1130}
                lenderBadge="Beviljar flest"
                liveTermsOverride={liveTermsByUcSlug.ferratum ?? null}
              />
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '0.05s' }}>
              <DynamicDetailedLoanCard
                provider="Fairlo"
                logo={fairloLogo}
                loanAmount="3 000 – 70 000 kr"
                nominalRate="22,00 %"
                effectiveRate="23,99 – 43,43 %"
                loanTerm="1 mån – 6 år"
                loanWithoutUC="Ja"
                responseTime="Direkt på skärmen"
                paymentSolution="Zimpler"
                link={`/${currentLanguage}/lana-utan-uc/fairlo`}
                entityType="lan-utan-uc"
                entitySlug="fairlo"
                fallbackRating={4.6}
                fallbackReviewCount={4352}
                liveTermsOverride={liveTermsByUcSlug.fairlo ?? null}
              />
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <DynamicDetailedLoanCard
                provider="Loanstep"
                logo={loanstepLogo}
                loanAmount="10 000 – 50 000 kr"
                nominalRate="19,95 – 21,95 %"
                effectiveRate="21,60 – 42,52 %"
                loanTerm="57-76 månader"
                loanWithoutUC="Ja"
                responseTime="Direkt på skärmen"
                paymentSolution="Banköverföring"
                link={`/${currentLanguage}/lana-utan-uc/loanstep`}
                entityType="lan-utan-uc"
                entitySlug="loanstep"
                fallbackRating={4.7}
                fallbackReviewCount={3494}
                liveTermsOverride={liveTermsByUcSlug.loanstep ?? null}
              />
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
              <DynamicDetailedLoanCard
                provider="Brixo"
                logo={brixoLogo}
                loanAmount="3 000 – 50 000 kr"
                nominalRate="21,95 %"
                effectiveRate="24,30 %"
                loanTerm="Flexibel"
                loanWithoutUC="Ja"
                responseTime="Inom 1 dygn"
                paymentSolution="Swish"
                link={`/${currentLanguage}/lana-utan-uc/brixo`}
                entityType="lan-utan-uc"
                entitySlug="brixo"
                fallbackRating={4.6}
                fallbackReviewCount={2969}
                liveTermsOverride={liveTermsByUcSlug.brixo ?? null}
              />
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <DynamicDetailedLoanCard
                provider="Flexkontot"
                logo={flexkontotLogo}
                loanAmount="3 000 – 40 000 kr"
                nominalRate="21,95 %"
                effectiveRate="36,40 %"
                loanTerm="Flexibel"
                loanWithoutUC="Ja"
                responseTime="Inom 1 dygn"
                paymentSolution="Swish"
                link={`/${currentLanguage}/lana-utan-uc/flexkontot`}
                entityType="lan-utan-uc"
                entitySlug="flexkontot"
                fallbackRating={4.7}
                fallbackReviewCount={1795}
                liveTermsOverride={liveTermsByUcSlug.flexkontot ?? null}
              />
            </div>
            
            <div className="animate-fade-in" style={{ animationDelay: '0.25s' }}>
              <DynamicDetailedLoanCard
                provider="Merax"
                logo={meraxLogo}
                loanAmount="3 000 – 40 000 kr"
                nominalRate="21,95 %"
                effectiveRate="42,10 %"
                loanTerm="Flexibel"
                loanWithoutUC="Ja"
                responseTime="Inom 1 dygn"
                paymentSolution="Swish"
                link={`/${currentLanguage}/lana-utan-uc/merax`}
                entityType="lan-utan-uc"
                entitySlug="merax"
                fallbackRating={4.7}
                fallbackReviewCount={1298}
                liveTermsOverride={liveTermsByUcSlug.merax ?? null}
              />
            </div>
          </div>
          
          <div className="text-center mt-8">
            
          </div>
        </div>
      </section>

      <ComparisonMethodology
        title="Så jämför vi lån utan UC"
        description="Lån utan UC kan vara rätt för mindre behov, men de är ofta dyrare än vanliga privatlån. Därför visar vi både fördelar, begränsningar och länkar till förklarande guider."
        items={[
          {
            title: "Kreditupplysning är inte valfri",
            body: "Alla seriösa långivare gör kreditprövning. Utan UC betyder att en annan kreditupplysning används, inte att kontroll saknas.",
          },
          {
            title: "Högre risk kräver tydligare varning",
            body: "Mindre lån utan UC har ofta hög effektiv ränta. Sidan ska därför vara tydlig med kostnad, belopp och återbetalningstid.",
          },
          {
            title: "Guide före klick vid osäkerhet",
            body: "Användare som inte förstår skillnaden mellan UC, Creditsafe och Dun & Bradstreet ska kunna läsa guiden innan de ansöker.",
          },
        ]}
      />

      <DecisionPathLinks
        title="Välj rätt väg innan du lånar utan UC"
        description="Lån utan UC är ofta dyrare än vanliga privatlån. Därför ska användaren snabbt kunna jämföra alternativ, läsa riskerna och gå till ett billigare låneflöde när det passar bättre."
        links={[
          {
            title: "Jämför lån utan UC",
            description: "Se långivare som använder alternativa kreditupplysningsföretag.",
            href: "#jamfor-lan-utan-uc",
            label: "Till jämförelsen",
            badge: "Direktval",
          },
          {
            title: "Läs hela guiden",
            description: "Förstå skillnaden mellan UC, Creditsafe och Dun & Bradstreet innan du ansöker.",
            href: `/${currentLanguage}/guider/lana-utan-uc`,
            label: "Läs guiden",
          },
          {
            title: "Kreditupplysning",
            description: "Se hur en kreditupplysning fungerar och varför den behövs även utan UC.",
            href: `/${currentLanguage}/guider/kreditupplysning`,
            label: "Förstå kontrollen",
          },
          {
            title: "Effektiv ränta",
            description: "Jämför verklig lånekostnad, inte bara nominell ränta eller månadsränta.",
            href: `/${currentLanguage}/guider/effektiv-ranta`,
            label: "Läs om ränta",
          },
          {
            title: "Jämför privatlån",
            description: "Om du kan ta ett vanligt privatlån kan kostnaden ofta bli lägre.",
            href: `/${currentLanguage}/privatlan`,
            label: "Se privatlån",
          },
          {
            title: "Låna utan säkerhet",
            description: "Få en bredare förklaring av blancolån och grundkrav.",
            href: `/${currentLanguage}/guider/lan-utan-sakerhet`,
            label: "Läs guiden",
          },
        ]}
      />

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            Vanliga frågor om lån utan UC
          </h2>
          
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-left">
                Vad betyder lån utan UC?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ett lån utan UC innebär att långivaren inte använder{" "}
                  <Link 
                    to={`/${currentLanguage}/guider/kreditupplysning`}
                    className="text-primary hover:underline font-medium"
                  >
                    kreditupplysning
                  </Link> från Upplysningscentralen (UC) när du ansöker. I stället används andra 
                  kreditupplysningsföretag, som Bisnode eller Creditsafe, vilket gör att din 
                  kreditvärdighet hos UC inte påverkas.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2">
              <AccordionTrigger className="text-left">
                Är det tryggt att låna hos långivare som inte använder UC?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p className="mb-4">
                  <strong>Ja</strong>, att en långivare använder ett annat kreditupplysningsföretag än UC 
                  betyder inte att långivaren är mindre seriös. Alla långivare här står under 
                  <strong> Finansinspektionens tillsyn</strong>, vilket innebär att de måste följa 
                  de lagar och regler som gäller för långivare i Sverige.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3">
              <AccordionTrigger className="text-left">
                Kan man få lån utan UC trots betalningsanmärkningar?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, du kan ofta få lån utan UC trots betalningsanmärkningar eftersom kraven vanligtvis 
                  är lägre för mindre lån. Många långivare bedömer din betalningsförmåga och ekonomi i 
                  stort, och om du uppfyller kraven i övrigt är en betalningsanmärkning sällan ett hinder.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4">
              <AccordionTrigger className="text-left">
                Kan jag ta flera lån utan UC samtidigt?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, det går att ta flera lån utan UC samtidigt, men det är viktigt att vara försiktig. 
                  Om du ansöker om för många lån på kort tid kan det skapa problem för din ekonomi och 
                  förmåga att betala tillbaka. Långivare kan också se flera samtidiga ansökningar som en 
                  risk, även om de inte använder UC. Det är viktigt att bara ta så mycket lån som du kan hantera.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5">
              <AccordionTrigger className="text-left">
                Vad händer om jag tar ett lån utan UC och inte kan betala?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Om du inte kan betala tillbaka ett lån utan UC i tid kan det leda till förseningar, 
                  påminnelseavgifter och eventuellt inkasso. Även om långivaren inte använder UC för 
                  kreditupplysningen, kan andra upplysningstjänster användas och eventuellt en 
                  betalningsanmärkning kan hamna hos andra kreditupplysningsföretag, vilket kan påverka 
                  din kreditvärdighet. Det är viktigt att alltid försöka hitta en lösning med långivaren 
                  om du inte kan betala.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6">
              <AccordionTrigger className="text-left">
                Kan jag ta ett lån utan UC om jag är ny i Sverige?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                <p>
                  Ja, det går ofta att ta ett lån utan UC även om du är ny i Sverige. Många långivare 
                  kräver att du har varit folkbokförd i Sverige under en viss period, vanligtvis minst 
                  6 månader eller 1 år, och att du har en stadig inkomst. Det kan dock vara svårare att 
                  få lånet godkänt om du inte har någon etablerad kredithistorik i Sverige, men vissa 
                  långivare beviljar lån till personer som är nyanlända.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      <div className="container max-w-4xl py-8">
        <ReviewedBy variant="default" />

        {/* CTA - Jämför lån utan UC */}
        <div className="text-center bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-6 md:p-8 mb-8 border border-primary/20">
          <h3 className="text-xl md:text-2xl font-bold mb-3">Redo att jämföra lån utan UC?</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Se alla långivare som erbjuder lån utan UC-påverkan och jämför villkor, räntor och återbetalningstider.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              onClick={() => {
                const section = document.getElementById('jamfor-lan-utan-uc');
                if (section) {
                  section.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-primary hover:bg-primary/90"
            >
              <ShieldAlert className="mr-2 h-5 w-5" />
              Jämför lån utan UC
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/guider/lana-utan-uc`)}
              className="bg-white hover:bg-primary hover:text-white transition-colors border-2"
            >
              Läs fullständig guide
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>

      <StickyCTA show={showStickyCTA}>
        <div className="container mx-auto max-w-5xl px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">LoanStep</p>
              <p className="truncate text-xs text-muted-foreground">Skydda kreditvärdigheten — svar direkt</p>
            </div>
            <Button asChild size="lg" className="whitespace-nowrap bg-primary text-white hover:bg-primary/90">
              <a
                href="https://go.adt284.net/t/t?a=1961010341&as=2024680921&t=2&tk=1"
                target="_blank"
                rel="sponsored nofollow noopener noreferrer"
              >
                Ansök hos LoanStep
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </StickyCTA>
    </div>;
};
export default LoanWithoutUCPage;
